import { useState, useMemo } from 'react';
import { Search, X, Plus, Mail, Users, Server, CheckCircle, Clock, XCircle } from 'lucide-react';
import './RecipientPicker.css';

/**
 * RecipientPicker Component
 *
 * Searchable picker for selecting emails, groups, and SFTP sites as report recipients.
 * Reuses the same UX pattern as the group member accordion.
 *
 * Props:
 *  - availableEmails: [{id, email, verificationStatus}]
 *  - availableGroups: [{id, name, emailCount}]
 *  - availableSftpSites: [{id, name, description}]
 *  - selectedEmailIds: [id, ...]
 *  - selectedGroupIds: [id, ...]
 *  - selectedSftpSiteIds: [id, ...]
 *  - onAddEmail(id), onRemoveEmail(id)
 *  - onAddGroup(id), onRemoveGroup(id)
 *  - onAddSftpSite(id), onRemoveSftpSite(id)
 *  - deliveryMethod: 'SIGNED_URL' | 'SFTP'
 *  - disabled: boolean
 */
export default function RecipientPicker({
  availableEmails = [],
  availableGroups = [],
  availableSftpSites = [],
  selectedEmailIds = [],
  selectedGroupIds = [],
  selectedSftpSiteIds = [],
  onAddEmail,
  onRemoveEmail,
  onAddGroup,
  onRemoveGroup,
  onAddSftpSite,
  onRemoveSftpSite,
  deliveryMethod = 'SIGNED_URL',
  disabled = false
}) {
  const isSignedUrl = deliveryMethod === 'SIGNED_URL';
  const tabs = isSignedUrl
    ? [{ key: 'emails', label: 'Emails', icon: Mail }, { key: 'groups', label: 'Groups', icon: Users }]
    : [{ key: 'sftp', label: 'SFTP Sites', icon: Server }];

  const [activeTab, setActiveTab] = useState(tabs[0].key);
  const [search, setSearch] = useState('');

  // Reset tab when delivery method changes
  const currentTab = tabs.find(t => t.key === activeTab) ? activeTab : tabs[0].key;

  const getVerificationIcon = (status) => {
    switch (status) {
      case 'verified': return <CheckCircle size={13} className="rp-status rp-verified" />;
      case 'failed': return <XCircle size={13} className="rp-status rp-failed" />;
      default: return <Clock size={13} className="rp-status rp-pending" />;
    }
  };

  // Filtered available items (not yet selected, matching search)
  const filteredEmails = useMemo(() => {
    const pool = availableEmails.filter(e => !selectedEmailIds.includes(e.id));
    if (!search) return pool;
    const term = search.toLowerCase();
    return pool.filter(e => e.email.toLowerCase().includes(term));
  }, [availableEmails, selectedEmailIds, search]);

  const filteredGroups = useMemo(() => {
    const pool = availableGroups.filter(g => !selectedGroupIds.includes(g.id));
    if (!search) return pool;
    const term = search.toLowerCase();
    return pool.filter(g => g.name.toLowerCase().includes(term));
  }, [availableGroups, selectedGroupIds, search]);

  const filteredSftp = useMemo(() => {
    const pool = availableSftpSites.filter(s => !selectedSftpSiteIds.includes(s.id));
    if (!search) return pool;
    const term = search.toLowerCase();
    return pool.filter(s => s.name.toLowerCase().includes(term));
  }, [availableSftpSites, selectedSftpSiteIds, search]);

  // Selected items resolved to full objects
  const selectedEmails = availableEmails.filter(e => selectedEmailIds.includes(e.id));
  const selectedGroups = availableGroups.filter(g => selectedGroupIds.includes(g.id));
  const selectedSftp = availableSftpSites.filter(s => selectedSftpSiteIds.includes(s.id));

  const renderAvailableList = () => {
    if (currentTab === 'emails') {
      if (filteredEmails.length === 0) return <div className="rp-empty">{search ? 'No matching emails' : 'All emails added'}</div>;
      return filteredEmails.slice(0, 8).map(e => (
        <button key={e.id} className="rp-available-item" type="button" onClick={() => onAddEmail(e.id)} disabled={disabled}>
          <span className="rp-item-label">{getVerificationIcon(e.verificationStatus)}{e.email}</span>
          <Plus size={14} />
        </button>
      ));
    }
    if (currentTab === 'groups') {
      if (filteredGroups.length === 0) return <div className="rp-empty">{search ? 'No matching groups' : 'All groups added'}</div>;
      return filteredGroups.slice(0, 8).map(g => (
        <button key={g.id} className="rp-available-item" type="button" onClick={() => onAddGroup(g.id)} disabled={disabled}>
          <span className="rp-item-label"><Users size={14} className="rp-group-icon" />@{g.name}<span className="rp-meta">{g.emailCount} emails</span></span>
          <Plus size={14} />
        </button>
      ));
    }
    if (currentTab === 'sftp') {
      if (filteredSftp.length === 0) return <div className="rp-empty">{search ? 'No matching sites' : 'All sites added'}</div>;
      return filteredSftp.slice(0, 8).map(s => (
        <button key={s.id} className="rp-available-item" type="button" onClick={() => onAddSftpSite(s.id)} disabled={disabled}>
          <span className="rp-item-label"><Server size={14} className="rp-sftp-icon" />{s.name}</span>
          <Plus size={14} />
        </button>
      ));
    }
  };

  const getOverflowCount = () => {
    if (currentTab === 'emails') return Math.max(0, filteredEmails.length - 8);
    if (currentTab === 'groups') return Math.max(0, filteredGroups.length - 8);
    if (currentTab === 'sftp') return Math.max(0, filteredSftp.length - 8);
    return 0;
  };

  return (
    <div className="recipient-picker">
      {/* Tabs */}
      <div className="rp-tabs">
        {tabs.map(tab => (
          <button
            type="button"
            key={tab.key}
            className={`rp-tab ${currentTab === tab.key ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setSearch(''); }}
            disabled={disabled}
          >
            <tab.icon size={15} />
            {tab.label}
            {tab.key === 'emails' && <span className="rp-tab-count">{selectedEmailIds.length}</span>}
            {tab.key === 'groups' && <span className="rp-tab-count">{selectedGroupIds.length}</span>}
            {tab.key === 'sftp' && <span className="rp-tab-count">{selectedSftpSiteIds.length}</span>}
          </button>
        ))}
      </div>

      <div className="rp-body">
        {/* Left: search + available */}
        <div className="rp-available-panel">
          <div className="rp-search">
            <Search size={15} />
            <input
              type="text"
              placeholder={currentTab === 'emails' ? 'Search emails...' : currentTab === 'groups' ? 'Search groups...' : 'Search SFTP sites...'}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="rp-search-input"
              disabled={disabled}
            />
            {search && (
              <button className="rp-clear" type="button" onClick={() => setSearch('')}><X size={13} /></button>
            )}
          </div>
          <div className="rp-available-list">
            {renderAvailableList()}
            {getOverflowCount() > 0 && (
              <div className="rp-overflow">+{getOverflowCount()} more — use search to filter</div>
            )}
          </div>
        </div>

        {/* Right: selected items */}
        <div className="rp-selected-panel">
          <h4 className="rp-selected-heading">
            {currentTab === 'emails' ? 'Selected Emails' : currentTab === 'groups' ? 'Selected Groups' : 'Selected SFTP Sites'}
          </h4>
          <div className="rp-selected-list">
            {currentTab === 'emails' && (
              selectedEmails.length === 0 ? <p className="rp-none">No emails selected</p> :
              selectedEmails.map(e => (
                <div key={e.id} className="rp-selected-item">
                  <span className="rp-item-label">{getVerificationIcon(e.verificationStatus)}{e.email}</span>
                  <button className="rp-remove" type="button" onClick={() => onRemoveEmail(e.id)} disabled={disabled}><X size={13} /></button>
                </div>
              ))
            )}
            {currentTab === 'groups' && (
              selectedGroups.length === 0 ? <p className="rp-none">No groups selected</p> :
              selectedGroups.map(g => (
                <div key={g.id} className="rp-selected-item">
                  <span className="rp-item-label"><Users size={14} className="rp-group-icon" />@{g.name}<span className="rp-meta">{g.emailCount} emails</span></span>
                  <button className="rp-remove" type="button" onClick={() => onRemoveGroup(g.id)} disabled={disabled}><X size={13} /></button>
                </div>
              ))
            )}
            {currentTab === 'sftp' && (
              selectedSftp.length === 0 ? <p className="rp-none">No SFTP sites selected</p> :
              selectedSftp.map(s => (
                <div key={s.id} className="rp-selected-item">
                  <span className="rp-item-label"><Server size={14} className="rp-sftp-icon" />{s.name}</span>
                  <button className="rp-remove" type="button" onClick={() => onRemoveSftpSite(s.id)} disabled={disabled}><X size={13} /></button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
